import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";

export const Route = createFileRoute("/unsubscribe")({
  validateSearch: (search: Record<string, unknown>) => ({
    token: typeof search.token === "string" ? search.token : "",
  }),
  head: () => ({
    meta: [
      { title: "Afmeld nyhedsbrev – Flâneriet" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: UnsubscribePage,
});

type State =
  | { status: "loading" }
  | { status: "invalid" }
  | { status: "already" }
  | { status: "ready" }
  | { status: "submitting" }
  | { status: "done" }
  | { status: "error"; message: string };

function UnsubscribePage() {
  const { token } = Route.useSearch();
  const [state, setState] = useState<State>({ status: "loading" });

  useEffect(() => {
    if (!token) {
      setState({ status: "invalid" });
      return;
    }
    fetch(`/email/unsubscribe?token=${encodeURIComponent(token)}`)
      .then(async (r) => {
        const body = await r.json().catch(() => ({}));
        if (!r.ok) {
          setState({ status: "invalid" });
          return;
        }
        if (body.reason === "already_unsubscribed") {
          setState({ status: "already" });
          return;
        }
        setState({ status: "ready" });
      })
      .catch(() => setState({ status: "invalid" }));
  }, [token]);

  async function onConfirm() {
    setState({ status: "submitting" });
    try {
      const r = await fetch("/email/unsubscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) {
        setState({ status: "error", message: "Kunne ikke afmelde. Prøv igen." });
        return;
      }
      if (body.reason === "already_unsubscribed") {
        setState({ status: "already" });
        return;
      }
      setState({ status: "done" });
    } catch {
      setState({ status: "error", message: "Kunne ikke afmelde. Prøv igen." });
    }
  }

  return (
    <div className="mx-auto max-w-2xl px-6 py-24 text-center">
      <p className="eyebrow">Nyhedsbrev</p>

      {state.status === "loading" && (
        <>
          <h1 className="mt-4 font-serif text-3xl">Et øjeblik…</h1>
        </>
      )}

      {state.status === "invalid" && (
        <>
          <h1 className="mt-4 font-serif text-3xl">Linket kan ikke bruges</h1>
          <p className="mt-4 meta italic">
            Måske er det udløbet. Skriv til mig, hvis du fortsat modtager mails.
          </p>
        </>
      )}

      {state.status === "already" && (
        <>
          <h1 className="mt-4 font-serif text-3xl">Du er allerede afmeldt</h1>
          <p className="mt-4 meta italic">Vi sender dig ikke flere breve.</p>
        </>
      )}

      {(state.status === "ready" || state.status === "submitting") && (
        <>
          <h1 className="mt-4 font-serif text-3xl">Vil du afmelde nyhedsbrevet?</h1>
          <p className="mt-4 meta italic">
            Vi holder op med at sende dig breve fra Flâneriet.
          </p>
          <button
            onClick={onConfirm}
            disabled={state.status === "submitting"}
            className="mt-8 font-sans text-sm border border-bordeaux px-5 py-2.5 text-bordeaux hover:bg-bordeaux hover:text-paper transition-colors duration-500 disabled:opacity-50"
          >
            {state.status === "submitting" ? "Afmelder…" : "Ja, afmeld mig"}
          </button>
        </>
      )}

      {state.status === "done" && (
        <>
          <h1 className="mt-4 font-serif text-3xl">Du er afmeldt</h1>
          <p className="mt-4 meta italic">Tak, fordi du var med.</p>
        </>
      )}

      {state.status === "error" && (
        <>
          <h1 className="mt-4 font-serif text-3xl">Noget gik galt</h1>
          <p className="mt-4 meta italic">{state.message}</p>
        </>
      )}

      <div className="mt-10">
        <a href="/" className="border-b border-bordeaux pb-0.5 text-bordeaux">
          Til forsiden
        </a>
      </div>
    </div>
  );
}
